import type { Dispatch, SetStateAction } from "react";

import {
  getDrugSubmissionAttachmentDescriptionPortal,
  getSubmissionPortal,
  patchSubmissionPortal,
  postSubmissionAttachmentPortal,
  postSubmissionAttachmentThumbnailPortal,
  postSubmissionAttachmentVectorPortal,
  postSubmissionStatusPortal,
} from "@rubi/api-generated";
import axios from "axios";
import { Model } from "survey-core";

export interface SurveyHandlerProps {
  id: string;
  appName: string;
  attachmentIDs: { [key: string]: string };
  setAttachmentIDs: Dispatch<SetStateAction<{ [key: string]: string }>>;
  organizationConfig?: {
    generateAttachmentDescriptions?: boolean;
  };
  navigate: (path: string, options?: any) => void;
}

const isValidEmail = (email: string) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const DESCRIPTION_API_KEY_TO_ATTACHMENT_PANEL_KEY: Record<string, string> = {
  incident_drugImpressions: "incident-evidence_drugImpressions",
  incident_packagingMarkings: "incident-evidence_packagingMarkings",
  incident_packagingMaterial: "incident-evidence_packagingMaterial",
};

export const createSurveyHandlers = (props: SurveyHandlerProps) => {
  const { id, attachmentIDs, setAttachmentIDs, organizationConfig } = props;

  /**
   * Save draft data only (version + contact fields). Used for progressive save on page change.
   * Does not change submission status.
   */
  const saveDraft = async (sender: any) => {
    const dataToSend = JSON.parse(JSON.stringify(sender.data));
    const traverse = (obj: any) => {
      for (const key in obj) {
        if (key === "content" && obj["content"]?.startsWith("data:")) {
          delete obj[key];
        } else if (typeof obj[key] === "object") {
          traverse(obj[key]);
        }
      }
    };
    traverse(dataToSend);

    const emailToUse = () => {
      if (
        dataToSend["person-reportingParty_email"] &&
        isValidEmail(dataToSend["person-reportingParty_email"])
      ) {
        return dataToSend["person-reportingParty_email"];
      }
      if (
        dataToSend["business-reportingParty_email"] &&
        isValidEmail(dataToSend["business-reportingParty_email"])
      ) {
        return dataToSend["business-reportingParty_email"];
      }
      return "";
    };

    const firstName = dataToSend["person-reportingParty_firstName"] ?? "";
    const lastName = dataToSend["person-reportingParty_lastName"] ?? "";
    const email = emailToUse() ?? "";
    const phoneNumber = dataToSend["person-reportingParty_phoneNo"] ?? "";

    await patchSubmissionPortal({
      path: { submission_id: id },
      body: {
        email,
        first_name: firstName,
        last_name: lastName,
        phone_number: phoneNumber,
        version: {
          submission: dataToSend,
          attachments: { ids: Object.values(attachmentIDs) },
        },
      },
    });
  };

  const submitForm = async (sender: any, options: any) => {
    options.showSaveInProgress();

    const currentStatus =
      options.currentStatus ??
      (
        await getSubmissionPortal({
          path: { submission_id: id },
          query: { version: "latest" },
        })
      ).data?.status ??
      "draft";

    try {
      const dataToSend = JSON.parse(JSON.stringify(sender.data));
      const traverse = (obj: any) => {
        for (const key in obj) {
          if (key === "content" && obj["content"]?.startsWith("data:")) {
            delete obj[key];
          } else if (typeof obj[key] === "object") {
            traverse(obj[key]);
          }
        }
      };
      traverse(dataToSend);

      const emailToUse = () => {
        if (
          dataToSend["person-reportingParty_email"] &&
          isValidEmail(dataToSend["person-reportingParty_email"])
        ) {
          return dataToSend["person-reportingParty_email"];
        }
        if (
          dataToSend["business-reportingParty_email"] &&
          isValidEmail(dataToSend["business-reportingParty_email"])
        ) {
          return dataToSend["business-reportingParty_email"];
        }
        return "";
      };

      const firstName = dataToSend["person-reportingParty_firstName"] ?? "";
      const lastName = dataToSend["person-reportingParty_lastName"] ?? "";
      const email = emailToUse() ?? "";
      const phoneNumber = dataToSend["person-reportingParty_phoneNo"] ?? "";

      await patchSubmissionPortal({
        path: { submission_id: id },
        body: {
          email: email,
          first_name: firstName,
          last_name: lastName,
          phone_number: phoneNumber,
          version: {
            submission: dataToSend,
            attachments: { ids: Object.values(attachmentIDs) },
          },
        },
      });

      await postSubmissionStatusPortal({
        path: { submission_id: id },
        body: {
          status:
            currentStatus === "draft"
              ? "submitted"
              : currentStatus === "modification_requested"
                ? "resubmitted"
                : currentStatus,
        },
      });

      options.showSaveSuccess();
    } catch (error) {
      console.error(error);
      options.showSaveError();
      return false;
    }
    return true;
  };

  const toBase64 = (file: File) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  const uploadFiles = async (survey: Model, options: any) => {
    const returnValues: { [key: string]: any } = [];
    for (const file of options.files) {
      const s3Details = (
        await postSubmissionAttachmentPortal({
          path: { submission_id: id },
          body: {
            filename: file.name,
            size: file.size,
            content_type: file.type,
            version_specific: false,
          },
        })
      ).data!;

      const headers: { [key: string]: string } = {};
      s3Details.s3_presigned.headers.forEach((header) => {
        headers[header.key] = header.value;
      });

      await axios.put(s3Details.s3_presigned.uri, file, { headers });

      const attachmentKey = options.name + file.name;
      // Merge onto the latest map rather than the snapshot this handler closed
      // over: uploads take seconds, and a removal completing in the meantime
      // would otherwise be undone, putting the removed id back in
      // `attachments.ids` and back into the export.
      setAttachmentIDs((prev) => ({ ...prev, [attachmentKey]: s3Details.id }));

      // Label for generated descriptions: this attachment's position in the map.
      const imageNumber = Object.keys({
        ...attachmentIDs,
        [attachmentKey]: s3Details.id,
      }).length;

      if (file.type.startsWith("image/")) {
        postSubmissionAttachmentThumbnailPortal({
          path: { submission_id: id, attachment_id: s3Details.id },
        }).catch((error) => {
          console.error("Error generating thumbnail", error);
        });
      }

      if (organizationConfig?.generateAttachmentDescriptions) {
        try {
          const { description } = (
            await getDrugSubmissionAttachmentDescriptionPortal({
              path: { submission_id: id, attachment_id: s3Details.id },
            })
          ).data!;
          const descriptionJSON = JSON.parse(description);

          const newData = { ...survey.data };
          for (const key in descriptionJSON) {
            newData[key] = newData[key]
              ? newData[key] +
                "\n\nImage " +
                imageNumber +
                ":\n" +
                descriptionJSON[key]
              : "Image " + imageNumber + ":\n" + descriptionJSON[key];
          }
          survey.data = newData;

          // Set per-panel description fields on the paneldynamic panel item
          const panel = options.question?.parent;
          if (panel) {
            for (const [apiKey, panelFieldName] of Object.entries(
              DESCRIPTION_API_KEY_TO_ATTACHMENT_PANEL_KEY,
            )) {
              if (descriptionJSON[apiKey]) {
                const panelQuestion = panel.getQuestionByName(panelFieldName);
                if (panelQuestion) {
                  panelQuestion.value = descriptionJSON[apiKey];
                } else {
                  // Field not in survey definition yet; set directly on panel data
                  panel.setValue(panelFieldName, descriptionJSON[apiKey]);
                }
              }
            }
          }
        } catch (error) {
          console.error("Error while setting attachment description", error);
        }
        await postSubmissionAttachmentVectorPortal({
          path: {
            attachment_id: s3Details.id,
            submission_id: id,
          },
        });
      }

      returnValues.push({
        id: s3Details.id,
        file: file,
        content: await toBase64(file),
      });
    }
    options.callback(returnValues);
  };

  const clearFiles = async (options: any) => {
    if (!options.value || options.value.length === 0) {
      return options.callback("success");
    }

    // Each branch drops keys from the latest map rather than the snapshot this
    // handler closed over, so a removal and an in-flight upload can't undo each
    // other's edits to `attachments.ids`.
    if (!options.fileName) {
      // Clear-all: drop every file's id in one pass. Deleting inside the loop
      // and returning on the first iteration left the rest of the ids in the
      // map, so they stayed in `attachments.ids` and were still exported.
      setAttachmentIDs((prev) => {
        const newIDs = { ...prev };
        for (const item of options.value) {
          delete newIDs[options.name + item.name];
        }
        return newIDs;
      });
      return options.callback("success");
    } else {
      const fileToRemove = options.value.find(
        (item: File) => item.name === options.fileName,
      );
      if (fileToRemove) {
        setAttachmentIDs((prev) => {
          const newIDs = { ...prev };
          delete newIDs[options.name + fileToRemove.name];
          return newIDs;
        });
        return options.callback("success");
      } else {
        console.error(`File with name ${options.fileName} is not found`);
      }
    }
  };

  return {
    submitForm,
    saveDraft,
    uploadFiles,
    clearFiles,
  };
};
